/**
 * SimonCat Agent Platform - Execution API Client
 *
 * Backend endpoints:
 * POST   /api/agents/:id/execute        Synchronous execution
 * POST   /api/agents/:id/execute/stream SSE streaming execution
 * GET    /api/agents/:id/executions     Execution history
 * GET    /api/agents/executions/:id     Execution details
 * GET    /api/agents/recent             Recent executions
 */

import axios from 'axios';

const API_BASE = '/api';

export interface ExecutionDTO {
  id?: number;
  executionId: string;
  agentId: string;
  userId?: number;
  input?: string;
  output?: string;
  status?: 'PENDING' | 'PLANNING' | 'RUNNING' | 'TOOL_CALLING' | 'COMPLETED' | 'FAILED' | 'TIMEOUT' | 'CANCELLED';
  providerCode?: string;
  modelName?: string;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  durationMs?: number;
  errorMessage?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SearchResult<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  tookMs?: number;
}

export interface ExecuteRequest {
  input: string;
  sessionId?: string;
  parameters?: Record<string, unknown>;
}

export async function executeAgent(agentId: string, data: ExecuteRequest): Promise<ExecutionDTO> {
  const resp = await axios.post<ExecutionDTO>(`${API_BASE}/agents/${agentId}/execute`, data);
  return resp.data;
}

export async function fetchExecutions(
  agentId: string,
  params?: { page?: number; size?: number }
): Promise<SearchResult<ExecutionDTO>> {
  const resp = await axios.get<SearchResult<ExecutionDTO>>(
    `${API_BASE}/agents/${agentId}/executions`,
    { params }
  );
  return resp.data;
}

export async function fetchExecution(executionId: string): Promise<ExecutionDTO> {
  const resp = await axios.get<ExecutionDTO>(`${API_BASE}/agents/executions/${executionId}`);
  return resp.data;
}

export async function fetchRecentExecutions(params?: {
  page?: number;
  size?: number;
}): Promise<SearchResult<ExecutionDTO>> {
  const resp = await axios.get<SearchResult<ExecutionDTO>>(
    `${API_BASE}/agents/recent`,
    { params }
  );
  return resp.data;
}

export function getStreamUrl(agentId: string): string {
  return `${API_BASE}/agents/${agentId}/execute/stream`;
}

export function createStreamExecution(
  agentId: string,
  data: ExecuteRequest,
  handlers: {
    onConnected?: () => void;
    onStarted?: (data: Record<string, unknown>) => void;
    onProgress?: (data: Record<string, unknown>) => void;
    onToolCall?: (data: Record<string, unknown>) => void;
    onToolResult?: (data: Record<string, unknown>) => void;
    onCompleted?: (data: Record<string, unknown>) => void;
    onError?: (data: Record<string, unknown>) => void;
    onConnectionError?: (error: Error) => void;
  }
): { abort: () => void } {
  const controller = new AbortController();
  const token = (() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem('simon-cat-auth') : null;
      if (!raw) return '';
      const parsed = JSON.parse(raw);
      return parsed?.state?.token || '';
    } catch {
      return '';
    }
  })();

  fetch(getStreamUrl(agentId), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: JSON.stringify(data),
    signal: controller.signal,
  })
    .then(async (resp) => {
      if (!resp.ok || !resp.body) {
        handlers.onError?.({ message: `HTTP ${resp.status}`, code: String(resp.status) });
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';

        for (const chunk of chunks) {
          if (!chunk.trim()) continue;
          const lines = chunk.split('\n');
          let eventName = 'message';
          let dataStr = '';

          for (const line of lines) {
            if (line.startsWith('event:')) {
              eventName = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              dataStr += line.slice(5).trim();
            }
          }

          if (!dataStr) continue;

          try {
            const parsed = JSON.parse(dataStr);
            switch (eventName) {
              case 'agent.connected':
                handlers.onConnected?.();
                break;
              case 'agent.started':
                handlers.onStarted?.(parsed);
                break;
              case 'agent.progress':
                handlers.onProgress?.(parsed);
                break;
              case 'agent.tool_call':
                handlers.onToolCall?.(parsed);
                break;
              case 'agent.tool_result':
                handlers.onToolResult?.(parsed);
                break;
              case 'agent.completed':
                handlers.onCompleted?.(parsed);
                break;
              case 'agent.error':
                handlers.onError?.(parsed);
                break;
            }
          } catch {
            // skip parse errors
          }
        }
      }
    })
    .catch((err) => {
      if (err.name !== 'AbortError') {
        handlers.onConnectionError?.(err);
      }
    });

  return { abort: () => controller.abort() };
}

/** @deprecated Named export for legacy code compatibility */
export const executionAPI = {
  executeAgent,
  fetchExecutions,
  fetchExecution,
  fetchRecentExecutions,
  createStreamExecution,
  getTasks: fetchRecentExecutions,
  mockGetTasks: fetchRecentExecutions,
};
